import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Tabs} from '@/components/ui/tabs'
import * as FormTabs from '@/components/ipevr/fila-form-tabs'
import type {FilaFormState, FilaFormTab} from '@/components/ipevr/fila-form-tabs'
import {useCreateFila, type Matriz} from '@/hooks/sgsst/riesgos/use-ipevr'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'

/** Se valida al enviar, no al salir: la pestaña Peligro solo cablea onChange. */
function validateDescripcion(raw: string): string | null {
  return validateRequired(raw)
}

export function AddFilaDialog({
  businessId,
  matriz,
  open,
  onOpenChange,
}: {
  businessId: string
  matriz: Matriz
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const create = useCreateFila(businessId)
  const [form, setForm] = useState<FilaFormState>(FormTabs.EMPTY_FILA_FORM)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<FilaFormTab>('peligro')
  const [descripcionError, setDescripcionError] = useState(false)

  function reset() {
    setForm(FormTabs.EMPTY_FILA_FORM)
    setError(null)
    setDescripcionError(false)
    setActiveTab('peligro')
  }

  function closeAndReset() {
    reset()
    onOpenChange(false)
  }

  const set = FormTabs.makeFilaFormFieldSetter(setForm)

  function submit(e: FormEvent) {
    e.preventDefault()
    if (validateDescripcion(form.descripcion)) {
      setDescripcionError(true)
      setActiveTab(FormTabs.FIELD_TAB.descripcion)
      return
    }
    setDescripcionError(false)
    create.mutate(FormTabs.filaFormToPayload(form), {
      onSuccess: closeAndReset,
      onError: (err) => setError(translateApiError(err)),
    })
  }

  function handleDialogOpenChange(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  const {nd: ndOptions, ne: neOptions, nc: ncOptions} = FormTabs.gtc45Options(matriz)
  const tabsWithErrors = new Set<FilaFormTab>(descripcionError ? [FormTabs.FIELD_TAB.descripcion] : [])

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden"
        data-testid="ipevr-add-fila-dialog">
        <DialogHeader>
          <DialogTitle>{t('ipevr.addFila.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col" data-testid="ipevr-add-fila-form" noValidate>
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as FilaFormTab)}
            className="flex min-h-0 min-w-0 flex-1 flex-col">
            <FormTabs.FilaFormTabsList testId="add-fila-tabs" tabsWithErrors={tabsWithErrors} />
            <div className="min-h-0 flex-1 overflow-y-auto py-4">
              <FormTabs.ContextoTabContent
                form={form}
                set={set}
                setForm={setForm}
                idPrefix="add"
                testIdPrefix="add-fila"
              />
              <FormTabs.PeligroTabContent
                form={form}
                set={set}
                setForm={setForm}
                idPrefix="add"
                testIdPrefix="add-fila"
                descripcionError={descripcionError}
              />
              <FormTabs.ControlesTabContent
                form={form}
                set={set}
                setForm={setForm}
                idPrefix="add"
                testIdPrefix="add-fila"
                matrizAreas={matriz.areas}
              />
              <FormTabs.EvaluacionTabContent
                form={form}
                set={set}
                idPrefix="add"
                testIdPrefix="add-fila"
                ndOptions={ndOptions}
                neOptions={neOptions}
                ncOptions={ncOptions}
              />
            </div>
          </Tabs>

          <div className="space-y-2 border-t border-border pt-3">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeAndReset}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {t('ipevr.addFila.submit')}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
